/**
 * Servizio di autenticazione per il marketplace signup pubblico.
 *
 * Replica il pattern di src/modules/benvenuto/services/LeadRegistrationService.ts:
 *  - Istanza Firebase Auth separata ('marketplace-auth') per evitare race condition
 *  - Google: signInWithPopup + fallback signInWithRedirect su mobile / popup blocked
 *  - Provider attivi su Firebase Auth: Email/Password + Google
 *
 * Lo scopo qui è SOLO ottenere i dati anagrafici (email, nome, cognome) per
 * pre-compilare il form. Non manteniamo la sessione: il signup vero usa
 * publicSignup CF + auto-login con email/password.
 */

import {
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  signOut,
  type User,
} from 'firebase/auth';
import { getMarketplaceAuth } from '../firebase';

export interface GoogleSignInResult {
  ok: true;
  user: {
    uid: string;
    email: string;
    displayName: string | null;
    nome: string | null;
    cognome: string | null;
    photoURL: string | null;
  };
}

export interface GoogleSignInError {
  ok: false;
  error_code: string;
  message?: string;
}

function splitDisplayName(
  displayName: string | null,
): { nome: string; cognome: string } {
  if (!displayName) return { nome: '', cognome: '' };
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) return { nome: parts[0], cognome: '' };
  return {
    nome: parts[0],
    cognome: parts.slice(1).join(' '),
  };
}

export async function signInWithGoogle(): Promise<
  GoogleSignInResult | GoogleSignInError
> {
  const auth = getMarketplaceAuth();
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });

  let user: User | null = null;
  try {
    const credential = await signInWithPopup(auth, provider);
    user = credential.user;
  } catch (err: unknown) {
    const code = (err as { code?: string }).code || '';
    const message = (err as { message?: string }).message || '';
    // Log dettagliato per diagnostica (visibile in DevTools console)
    console.error('[MarketplaceAuth] Google sign-in failed', {
      code,
      message,
      err,
    });

    if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user') {
      // Fallback redirect (l'utente tornera' a /iscriviti dopo redirect)
      try {
        await signInWithRedirect(auth, provider);
        // Nota: dopo redirect la pagina si ricarica, questa Promise non risolve
        // Ritorniamo per soddisfare TS ma il codice non arriva qui dopo redirect
        return { ok: false, error_code: 'auth.google.redirect_in_progress' };
      } catch (_redirectErr) {
        return { ok: false, error_code: 'auth.google.redirect_failed' };
      }
    }
    if (code === 'auth/cancelled-popup-request') {
      return { ok: false, error_code: 'auth.google.cancelled' };
    }
    if (code === 'auth/account-exists-with-different-credential') {
      return {
        ok: false,
        error_code: 'auth.google.email_already_used_different_provider',
      };
    }
    if (code === 'auth/unauthorized-domain') {
      return { ok: false, error_code: 'auth.google.unauthorized_domain' };
    }
    if (code === 'auth/operation-not-allowed') {
      return { ok: false, error_code: 'auth.google.provider_disabled' };
    }
    if (code === 'auth/network-request-failed') {
      return { ok: false, error_code: 'auth.google.network_error' };
    }
    if (code === 'auth/internal-error') {
      return { ok: false, error_code: 'auth.google.internal_error' };
    }
    return {
      ok: false,
      error_code: 'auth.google.unknown_error',
      message: message,
    };
  }

  if (!user || !user.email) {
    return { ok: false, error_code: 'auth.google.missing_email' };
  }

  const split = splitDisplayName(user.displayName);
  return {
    ok: true,
    user: {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      nome: split.nome || null,
      cognome: split.cognome || null,
      photoURL: user.photoURL,
    },
  };
}

export async function signOutMarketplace(): Promise<void> {
  await signOut(getMarketplaceAuth());
}
